
import * as React from "react";
import type { Person } from "@/lib/types";
import { makeInitials } from "@/lib/constants";
import { requestGraphConsent } from "@/lib/auth";
import {
  searchDirectory,
  MIN_QUERY,
  MAX_RESULTS,
  type DirectoryPerson,
  type DirectoryResult,
  type DirectorySearch,
} from "@/lib/directory";
import { Avatar } from "./primitives";
import { Icon } from "./icons";

// Directory-backed owner picker.
//
// Owners come from the company directory only (decided 2026-09-10). Typing
// searches two places: people already on the roadmap (the roster — only those
// with an email, i.e. who came from the directory or signed in themselves),
// then Microsoft Graph for everyone else. There is deliberately no "add
// 'Name'" affordance: a typed name has no email, and an owner without an
// email can't be reached by the Slack integration.
//
// Picking a directory hit hands name + email to onPickPerson (the store's
// create-or-reuse addPerson), which returns the roster row to reference.

export const DEBOUNCE_MS = 250;

type LinkedPerson = Person & { email: string };
type RosterRow = { kind: "roster"; key: string; person: LinkedPerson };
type DirectoryRow = { kind: "directory"; key: string; person: DirectoryPerson };
type Row = RosterRow | DirectoryRow;

const isLinked = (p: Person): p is LinkedPerson => !!p.email;

// The badge for an owner who was typed in by hand before owners became
// directory-only. Used on the selected chip here and in the drawer's read view.
export function UnlinkedBadge() {
  return (
    <span
      className="dri-unlinked"
      title="Added by hand before owners were directory-only, so there's no email to reach them at. Pick them from the directory to link it."
    >
      Not in directory
    </span>
  );
}

export function DriPicker({
  roster,
  value,
  onChange,
  onPickPerson,
  search = searchDirectory,
  requestConsent = requestGraphConsent,
  placeholder = "Search the directory…",
  inputId,
}: {
  roster: Person[];
  value: string;
  onChange: (id: string) => void;
  onPickPerson: (name: string, email: string) => Person;
  /** Injectable for tests; defaults to the Graph-backed search. Must be
   *  referentially stable — it is a dependency of the debounce timer. */
  search?: DirectorySearch;
  /** Interactive Graph consent, called from the "Connect directory" click. */
  requestConsent?: () => Promise<string | null>;
  placeholder?: string;
  inputId?: string;
}) {
  const selected = roster.find(p => p.id === value) ?? null;
  const [query, setQuery] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const [result, setResult] = React.useState<DirectoryResult | null>(null);
  const [searching, setSearching] = React.useState(false);
  const [active, setActive] = React.useState(0);
  // Bumped after a successful consent so the search effect runs again.
  const [attempt, setAttempt] = React.useState(0);
  const [connecting, setConnecting] = React.useState(false);
  const wrapRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const listId = React.useId();

  React.useEffect(() => {
    if (!open) return;
    const handle = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [open]);

  const q = query.trim();
  const qLower = q.toLowerCase();
  const canSearch = q.length >= MIN_QUERY;

  // Debounced directory search. The previous request is abandoned when the
  // query moves on so a slow early response can't overwrite a later one, and
  // the old result is dropped immediately so stale rows aren't pickable while
  // the new search is pending.
  React.useEffect(() => {
    setResult(null);
    if (!open || !canSearch) {
      setSearching(false);
      return;
    }
    setSearching(true);
    const ctrl = new AbortController();
    const timer = setTimeout(() => {
      search(q, ctrl.signal)
        .then(r => {
          if (ctrl.signal.aborted) return;
          setResult(r);
          setSearching(false);
        })
        .catch(err => {
          if (ctrl.signal.aborted) return;
          setResult({ status: "error", message: String(err?.message ?? err) });
          setSearching(false);
        });
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      ctrl.abort();
    };
  }, [open, canSearch, q, search, attempt]);

  // A new query starts the keyboard cursor over.
  React.useEffect(() => { setActive(0); }, [q]);

  // Roster section: only directory-linked people are offered as owners.
  const rosterRows: RosterRow[] = roster
    .filter(isLinked)
    .filter(p => !qLower || p.name.toLowerCase().includes(qLower) || p.email.toLowerCase().includes(qLower))
    .map(p => ({ kind: "roster", key: `r:${p.id}`, person: p }));
  const shownEmails = new Set(rosterRows.map(r => r.person.email.toLowerCase()));

  const okResult = result?.status === "ok" ? result : null;
  const directoryPeople = okResult?.people ?? [];
  const directoryRows: DirectoryRow[] = directoryPeople
    .filter(d => !shownEmails.has(d.email))
    .map(d => ({ kind: "directory", key: `d:${d.id}`, person: d }));

  const rows: Row[] = [...rosterRows, ...directoryRows];
  // Clamp the cursor to the live list during render — the list reshapes as
  // results arrive, and a stale index must never point past the end.
  const activeIdx = rows.length ? Math.min(active, rows.length - 1) : 0;

  const pick = (row: Row) => {
    if (row.kind === "roster") {
      onChange(row.person.id);
    } else {
      const person = onPickPerson(row.person.name, row.person.email);
      onChange(person.id);
    }
    setQuery("");
    setOpen(false);
  };

  const clear = () => {
    onChange("");
    setQuery("");
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const connect = async () => {
    setConnecting(true);
    try {
      const token = await requestConsent();
      if (token) setAttempt(a => a + 1);
      else setResult({ status: "unavailable" });
    } finally {
      setConnecting(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      if (rows.length) setActive((activeIdx + 1) % rows.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (rows.length) setActive((activeIdx - 1 + rows.length) % rows.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const row = rows[activeIdx];
      if (row) pick(row);
    } else if (e.key === "Escape") {
      // Escape closes the list first. The drawer and the create modal both
      // close on a document-level Escape — swallow it here so dismissing the
      // suggestions never throws away the half-edited form around them.
      if (open) {
        e.preventDefault();
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();
      }
      setOpen(false);
    }
  };

  const optionId = (i: number) => `${listId}-opt-${i}`;

  const renderOption = (row: Row, i: number) => {
    const p = row.person;
    return (
      <div
        key={row.key}
        id={optionId(i)}
        role="option"
        aria-selected={i === activeIdx}
        className={`popover-item${i === activeIdx ? " is-active" : ""}`}
        onMouseEnter={() => setActive(i)}
        onClick={() => pick(row)}
      >
        {row.kind === "roster"
          ? <Avatar person={row.person} size="sm" />
          : <span className="avatar sm dri-avatar-directory">{makeInitials(p.name)}</span>}
        <span className="dri-row-text">
          <span>{p.name}</span>
          <span className="dri-email">
            {p.email}{row.kind === "directory" && row.person.title ? ` · ${row.person.title}` : ""}
          </span>
        </span>
      </div>
    );
  };

  const note = (children: React.ReactNode) => (
    <div className="popover-item dri-note" role="status">{children}</div>
  );

  return (
    // min-width:0 / max-width:100% keep the picker inside whatever cell holds
    // it. Without them the chip's min-content width — an unbreakable email like
    // nathaniel.williams@lennar.com — forces the whole grid column wider and
    // the owner row spills into the column beside it.
    <div ref={wrapRef} style={{ position: "relative", minWidth: 0, maxWidth: "100%" }}>
      {selected && !open ? (
        <div
          role="button"
          tabIndex={0}
          // role=button + aria-label suppresses the chip's contents, so the
          // address has to be in the name itself — otherwise a screen reader
          // never gets it, and `title` is hover-only (no keyboard, no touch).
          aria-label={`Owner: ${selected.name}${selected.email ? `, ${selected.email}` : ", not linked to a directory account"}. Press Enter to change.`}
          onClick={() => setOpen(true)}
          onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen(true); } }}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            border: "1px solid var(--hairline)",
            borderRadius: 6,
            padding: "6px 10px",
            background: "var(--surface)",
            cursor: "pointer",
            fontSize: 13.5,
            minWidth: 0,
          }}
        >
          <Avatar person={selected} size="sm" />
          <span style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
            {/* The name wraps rather than truncating — see .dri-name. Only the
                address ellipsizes, and its title is the sighted-hover route;
                the accessible name above carries both for everyone else. */}
            <span className="dri-name">{selected.name}</span>
            {selected.email
              ? <span className="dri-email" title={selected.email}>{selected.email}</span>
              : <UnlinkedBadge />}
          </span>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); clear(); }}
            aria-label="Clear owner"
            style={{ border: "none", background: "transparent", color: "var(--muted-ink)", padding: 2 }}
          >
            <Icon name="x" size={12} />
          </button>
        </div>
      ) : (
        <input
          ref={inputRef}
          id={inputId}
          // Tailwind's preflight strips inputs bare. `.field input` re-dresses
          // the one in the create modal, but the initiative modal puts this in
          // a .kv cell where nothing did — it rendered as naked text beside
          // bordered selects. `.edit-inline` is that modal's control style.
          // In the create modal `.field input` (0,1,1) still beats it (0,1,0)
          // on every shared property, so the chrome there is unchanged —
          // .dri-input only supplies the width neither host stretches for us.
          className="edit-inline dri-input"
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={open && rows.length ? optionId(activeIdx) : undefined}
          autoComplete="off"
          autoFocus={!!selected}
          placeholder={placeholder}
          value={query}
          onFocus={() => setOpen(true)}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onKeyDown={onKeyDown}
        />
      )}
      {open && (
        <div
          id={listId}
          role="listbox"
          // .dri-list replaces the inline minWidth:100% that squeezed this to
          // the field's width (~155px in the initiative modal's rail, where
          // directory rows wrapped to three lines). Placement lives in the
          // stylesheet because it differs per host — see the rules in
          // index.css; inline offsets here would override them.
          className="popover dri-list"
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            zIndex: 80,
          }}
        >
          {rosterRows.length > 0 && (
            <>
              <div className="popover-section" role="presentation">On the roadmap</div>
              {rosterRows.map((row, i) => renderOption(row, i))}
            </>
          )}

          {canSearch && (
            <>
              {rosterRows.length > 0 && <div className="popover-divider" role="presentation" />}
              <div className="popover-section" role="presentation">Directory</div>
              {directoryRows.map((row, j) => renderOption(row, rosterRows.length + j))}
              {(searching || !result) && note("Searching the directory…")}
              {!searching && okResult && directoryRows.length === 0 && note(
                directoryPeople.length > 0
                  ? "Everyone matching is already on the roadmap (above)."
                  : <>No one in the directory matches &ldquo;{q}&rdquo;.</>
              )}
              {!searching && okResult?.truncated && directoryRows.length > 0 && note(
                `Showing the first ${MAX_RESULTS} matches. Keep typing to narrow it down.`
              )}
              {!searching && result?.status === "consent-needed" && (
                <div className="popover-item dri-note" role="status">
                  <span>
                    The roadmap needs your OK to look people up in the company directory.{" "}
                    <button type="button" className="dri-connect" onClick={connect} disabled={connecting}>
                      {connecting ? "Connecting…" : "Connect directory"}
                    </button>
                  </span>
                </div>
              )}
              {!searching && result?.status === "unavailable" && note(
                "Directory search isn't available right now. You can still pick someone already on the roadmap."
              )}
              {!searching && result?.status === "error" && note(result.message)}
            </>
          )}

          {!canSearch && rosterRows.length === 0 && note("Type at least two letters to search the directory.")}
        </div>
      )}
    </div>
  );
}
